import axios from 'axios';
import { fetchCSRFToken } from './urlUtils';
import { affectsGatewayConfig, isConfigSave, notifyConfigChanged } from './configSyncNotifier';
import { classifyAuthError } from './apiErrors';
import { emitPermissionDenied } from './permissionDeniedBus';
import { canAccessAdminNow } from './identityStore';
import { redirectToLogin } from './authRedirect';
import { csrfTokenHeader, withBase } from '../../runtimeConfig';

let apiClientInstance = null;

const createApiClient = () => {
  const instance = axios.create({
    baseURL: withBase('/api/v1'),
    withCredentials: true,
  });

  instance.interceptors.request.use(
    async (config) => {
      if (config.method !== 'get') {
        const token = await fetchCSRFToken();
        if (token) {
          config.headers[csrfTokenHeader()] = token;
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => {
      // Saving something the edge gateways care about does not take effect
      // until a config push. Refresh sync status now rather than leaving the
      // banner up to a 30-second poll, which is the window in which people
      // conclude their change did nothing.
      const { method, url } = response?.config || {};
      if (affectsGatewayConfig(method, url)) {
        notifyConfigChanged({ saved: isConfigSave(method, url) });
      }
      return response;
    },
    (error) => {
      const status = error.response?.status;
      if (status === 401) {
        // Handle unauthorized access (public pages stay where they are)
        redirectToLogin();
        return Promise.reject(error);
      }
      if (status === 403 || status === 402) {
        // Turn authorization failures into typed errors so pages can tell a
        // missing permission apart from a missing licence. Denied mutations
        // are announced once, globally; reads are left to the page (a
        // route guard normally prevents them anyway).
        const typed = classifyAuthError(error);
        if (typed?.isPermissionDenied) {
          const method = (error.config?.method || 'get').toLowerCase();
          if (method !== 'get' && !error.config?.silent403) {
            emitPermissionDenied(typed);
          }
        }
        return Promise.reject(typed);
      }
      return Promise.reject(error);
    }
  );

  // No method overrides


  return instance;
};

// Initialize the API client instance
apiClientInstance = createApiClient();

// App-Tool API endpoints
export const appToolAPI = {
  getAppTools: (appId) => apiClientInstance.get(`/apps/${appId}/tools`),
  addToolToApp: (appId, toolId) => apiClientInstance.post(`/apps/${appId}/tools/${toolId}`),
  removeToolFromApp: (appId, toolId) => apiClientInstance.delete(`/apps/${appId}/tools/${toolId}`),
  // Assuming a general endpoint to get all available tools for selection
  // Adjust if tools are fetched differently (e.g., from tool-catalogues)
  listAvailableTools: (params) => apiClientInstance.get('/tools', { params: { all: true, ...params } }),
};

// Plugin RPC call function for secure plugin communication
export const pluginRPCCall = async (pluginId, method, payload = {}) => {
  // Security: only users on the administration surface may call plugin RPCs;
  // the server enforces the plugins:execute permission.
  if (!canAccessAdminNow()) {
    throw new Error('Plugin RPC calls require admin permissions');
  }

  // Call through secure apiClient (handles auth, CSRF, CORS)
  try {
    const response = await apiClientInstance.post(`/plugins/${pluginId}/rpc/${method}`, payload);
    return response.data;
  } catch (error) {
    console.error(`Plugin RPC call failed: ${method}`, error);
    throw error;
  }
};

// Function to reinitialize the API client with new configuration
export const reinitializeApiClient = () => {
  apiClientInstance = createApiClient();
  return apiClientInstance;
};

export default apiClientInstance;
